import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useIsFetching, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppMessage } from '@/common/components';
import { AppRoutes, BaseQueryParams, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { PageNodeFormFields, PageNodeFormInputs, PageNodesQueryKeys, useCreatePageNodeMutation, useGetByIdPageNodeQuery } from '@/modules/Pages';

import { PageNodesFormView } from './PageNodesFormView';
import { pageNodesValidationSchema } from './validation';

interface Props {
  siteId: string;
  parentNodeId: string;
}

export const PageNodesCreateFormContainer: FC<Props> = memo(({ parentNodeId, siteId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageNodeFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageNodeFormInputs>>(null);

  const nodeData = queryClient.getQueryData(
    [PageNodesQueryKeys.GetOneById, siteId, parentNodeId], {
      exact: true
    }
  ) as BaseQueryParams<typeof useGetByIdPageNodeQuery>['data'];

  useIsFetching([PageNodesQueryKeys.GetOneById, siteId, parentNodeId], {
    exact: true
  });

  const nodeQueryState = queryClient.getQueryState([PageNodesQueryKeys.GetOneById, siteId, parentNodeId], {
    exact: true
  });

  const isNodeLoading = !nodeQueryState?.status || nodeQueryState?.status === 'loading';
  const isNodeError = !!nodeQueryState?.error;

  const { mutate: createPageNodeMutation, isLoading: editPageNodeIsLoading } = useCreatePageNodeMutation(
    siteId
  )({
    onSuccess: (newNode):void => {
      appMessageApi.success('Ноду створено успішно!');

      navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', newNode.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageNodeFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: PageNodeFormFields = {
    slug: undefined,
    extension: undefined,
    parentNodeId: nodeData?.id,
    siteId
  };

  const handleFinish = useCallback((values: PageNodeFormFields): void => {
    createPageNodeMutation(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', parentNodeId));
  }, [siteId, parentNodeId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout isLoading={ isNodeLoading } isError={ isNodeError } error={ null }>
      {
        () => (
          <PageNodesFormView
            isNodeHome={ false }
            siteId={ siteId }
            formErrors={ formErrors }
            validationSchema={ pageNodesValidationSchema }
            formName={ 'page-node-create' }
            formType={ 'create' }
            form={ form }
            initialValues={ initialValues }
            isFormLoading={ editPageNodeIsLoading }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
